import { oc } from '@orpc/contract'
import { z } from 'zod'

export const documentTagInlineSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
})

export const documentSchema = z.object({
  id: z.string().min(1),
  projectId: z.string().min(1),
  title: z.string(),
  slug: z.string(),
  tags: z.array(documentTagInlineSchema),
  sourceFilePath: z.string().nullable().optional(),
  conversionStatus: z.enum(['pending', 'converting', 'completed', 'failed']).nullable().optional(),
  /** Bytes of the uploaded original; null for editor documents. */
  sizeBytes: z.number().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const documentsListContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      tagId: z.string().optional(),
    })
  )
  .output(
    z.object({
      items: z.array(documentSchema),
    })
  )

export const documentsGetContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      documentId: z.string().min(1),
    })
  )
  .output(
    z.object({
      document: documentSchema,
      markdownContent: z.string().nullable().optional(),
      pdfPath: z.string().nullable().optional(),
      /** CSV to show in the spreadsheet viewer (uploaded .csv, or the CSV rendered from .xls/.xlsx). */
      spreadsheetPath: z.string().nullable().optional(),
      /**
       * Image documents (board card 2u86): the picture to show — the original,
       * or `_processed/<id>.jpg` for HEIC/HEIF once the pipeline wrote it.
       */
      imagePath: z.string().nullable().optional(),
      /**
       * Image documents: the AI description + visible text (markdown, read-only),
       * null while it is being written or when there is none.
       */
      description: z.string().nullable().optional(),
      originalFileName: z.string().nullable().optional(),
    })
  )

export const documentsCreateContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      title: z.string().min(1),
    })
  )
  .output(documentSchema)

export const documentsUpdateContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      documentId: z.string().min(1),
      title: z.string().min(1).optional(),
    })
  )
  .output(documentSchema)

export const documentsSetTagsContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      documentId: z.string().min(1),
      tagIds: z.array(z.string().min(1)).max(5),
    })
  )
  .output(documentSchema)

export const documentsDeleteContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      documentId: z.string().min(1),
    })
  )
  .output(
    z.object({
      success: z.boolean(),
    })
  )

export const documentsReindexContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
    })
  )
  .output(
    z.object({
      triggered: z.number(),
    })
  )

export const documentsReindexOneContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      documentId: z.string().min(1),
    })
  )
  .output(
    z.object({
      success: z.boolean(),
    })
  )

export const documentsRegenerateMarkdownContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
    })
  )
  .output(
    z.object({
      triggered: z.number(),
    })
  )

/** Undo every block change an agent turn made to a document (PLAN-079 §4.3). */
export const documentsUndoAgentTurnContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      documentId: z.string().min(1),
      turnId: z.string().min(1),
    })
  )
  .output(z.object({ undone: z.number().int() }))
